import { FacetEnum, FtpfImportResult, FtpfImportTypeEnum, useLazyAzureImportQuery } from ".codegen/generatedApi";
import { Cancel, CloudDownload } from "@mui/icons-material";
import { Box, Button, Checkbox, Container, Divider, FormControl, FormControlLabel, FormGroup, FormLabel, LinearProgress, Radio, Stack, TextField, Typography } from "@mui/material";
import { addToDistinctArray, removeFromDistinctArray } from 'helpers';
import React from 'react';
import './Import.scss'

enum STOP_REASON {
  NONE,
  CANCELLED,
  COMPLETE,
  ERROR,
}

enum GO_REASON {
  USER,
  AUTOLOAD,
}

enum STATUS {
  IDLE,
  WORKING,
  ERROR,
}

type ProgressSummaryProps = {
  value?: number | null
  total?: number | null
  unitSingle?: string
  unitPlural?: string
  loadedString?: string
  showUnits?: boolean
  isWorking?: boolean
}

const ProgressSummary = ({
  isWorking = false,
  loadedString = 'loaded',
  showUnits = true,
  total,
  unitPlural = 'items',
  unitSingle = 'item',
  value,
}: ProgressSummaryProps) => {
  let determinate = (total ?? value ?? null) !== null

  const unitsTotalLabel = total === 1 ? unitSingle : unitPlural

  const percentage = total ? (value ? Math.round((value / total) * 10000) / 100 : 0) : 100

  return (
    <Box marginY={2}>
      <Box>{determinate && <Typography variant="caption">{}</Typography>}</Box>
      {/* <Divider/> */}
      {determinate && showUnits && (
        <Typography variant="caption">{`${value}/${total || 0} ${unitsTotalLabel} ${loadedString} (${Math.round(
          percentage
        )}%)`}</Typography>
      )}
      {determinate && <LinearProgress variant="determinate" sx={{ height: isWorking ? 6 : 10 }} value={percentage} />}
      {isWorking && <LinearProgress sx={{ height: 2 }} />}
    </Box>
  )
}

type FacetEnumType = typeof FacetEnum
type FacetEnumKey = keyof FacetEnumType

const Import = () => {
  const [status, setStatus] = React.useState<STATUS>(STATUS.IDLE)
  const [stopReason, setStopReason] = React.useState<STOP_REASON>(STOP_REASON.NONE)

  const [autoload, setAutoload] = React.useState(true)
  const [type, setType] = React.useState<`${FtpfImportTypeEnum}`>(FtpfImportTypeEnum.Product)
  const [facets, setFacets] = React.useState<FacetEnumKey[]>([])
  const [facetCount, setFacetCount] = React.useState<number>(1000)
  const [updateOnly, setUpdateOnly] = React.useState<boolean>(false)
  const [page, setPage] = React.useState<number>(1)
  const [pageSize, setPageSize] = React.useState<number>(20)
  const [results, setResults] = React.useState<FtpfImportResult | null>(null)

  const [triggerImport, { data = {}, isLoading = false, isFetching = false } = {}] = useLazyAzureImportQuery()
  const responseData: FtpfImportResult | null = data.azureImport || null

  React.useEffect(() => {
    setResults(responseData)
  }, [responseData])

  const isWorking = isFetching || isLoading

  const {
    nProductsProcessed = null,
    nextPage = null,
    skippedProducts = null,
    totalProducts = null,
    // totalFacets = null,
    totalTerms = null,
  } = results || {}

  const productsLoaded = nProductsProcessed !== null ? (skippedProducts || 0) + nProductsProcessed : null

  const stop = React.useCallback((reason: STOP_REASON) => {
    switch (reason) {
      case STOP_REASON.CANCELLED:
        setStatus(STATUS.IDLE)
        break
      case STOP_REASON.COMPLETE:
        setStatus(STATUS.IDLE)
        break
      case STOP_REASON.ERROR:
        setStatus(STATUS.ERROR)
        break
    }
    console.log(STOP_REASON[reason])

    setStopReason(reason)
  }, [])

  const go = React.useCallback(
    (reason: GO_REASON, page: number = 1) => {
      console.log({ reason: GO_REASON[reason], autoload })
      if (reason === GO_REASON.AUTOLOAD && [STOP_REASON.CANCELLED, STOP_REASON.COMPLETE].includes(stopReason)) return

      if (reason === GO_REASON.AUTOLOAD && !autoload) return stop(STOP_REASON.COMPLETE)

      if (reason === GO_REASON.USER) setResults(null)
      setStatus(STATUS.WORKING)
      setStopReason(STOP_REASON.NONE)

      triggerImport({
        page,
        facetCount,
        facets: facets.map((k) => FacetEnum[k]),
        pageSize,
        type: type as FtpfImportTypeEnum,
        updateOnly,
      })
    },
    [autoload, stopReason, facetCount, facets, pageSize, type, updateOnly, triggerImport, stop]
  )

  React.useEffect(() => {
    if (nextPage === 0) return stop(STOP_REASON.COMPLETE)

    if (nextPage) go(GO_REASON.AUTOLOAD, nextPage)
  }, [go, stop, nextPage])

  const progressSummary =
    type === 'FACET' ? (
      <ProgressSummary total={totalTerms} isWorking={status === STATUS.WORKING} value={totalTerms} />
    ) : (
      <ProgressSummary total={totalProducts} isWorking={status === STATUS.WORKING} value={productsLoaded} />
    )
  const allFacets = Object.keys(FacetEnum) as FacetEnumKey[]
  const nFacets = allFacets.length
  const allFacetsSelected = facets.length === nFacets

  const handleFacetChange = (f: FacetEnumKey, checked: boolean) => {
    setFacets(checked ? addToDistinctArray(facets, f) : removeFromDistinctArray(facets, f))
  }

  const facetOptions = (
    <>
      <FormControlLabel
        label={'Select all'}
        control={<Checkbox checked={allFacetsSelected} onChange={(e, v) => (v ? setFacets(allFacets) : setFacets([]))} />}
      />
      <Divider />
      {allFacets.map((t, i) => (
        <FormControlLabel
          key={i}
          label={FacetEnum[t]}
          control={<Checkbox checked={facets.includes(t)} onChange={(e, v) => handleFacetChange(t, v)} />}
        />
      ))}
    </>
  )

  const typeOptions = (Object.values(FtpfImportTypeEnum) as `${FtpfImportTypeEnum}`[]).map((t, i) => {
    return <FormControlLabel key={i} label={t} control={<Radio checked={type === t} onChange={(e, v) => (v ? setType(t) : null)} />} />
  })

  const handleSubmit: React.FormEventHandler = (ev) => {
    ev.preventDefault()
    go(GO_REASON.USER, 1)
  }

  return (
    <Container id="product-finder-import" maxWidth={'sm'}>
      <Stack marginY={2} component={'form'} direction={'column'} onSubmit={handleSubmit} spacing={4} bgcolor={'grey.100'} padding={2}>
        <FormControl disabled={status !== STATUS.IDLE}>
          <FormLabel>Import Type</FormLabel>
          <FormGroup sx={{ pl: 1 }} row>
            {typeOptions}
          </FormGroup>
        </FormControl>

        {type === 'PRODUCT' && (
          <>
            <TextField
              disabled={status !== STATUS.IDLE}
              type={'number'}
              label={'Start Page'}
              value={page}
              onChange={(e) => setPage(Math.max(1, Number(e.target.value)))}
            />
            <TextField
              disabled={status !== STATUS.IDLE}
              type={'number'}
              label={'Page Size'}
              value={pageSize}
              onChange={(e) => setPageSize(Math.max(1, Number(e.target.value)))}
              helperText="If you're experiencing errors, try lowering this number."
            />
            <FormControlLabel
              disabled={status !== STATUS.IDLE}
              label={'Update only'}
              control={<Checkbox checked={updateOnly} onChange={() => setUpdateOnly(!updateOnly)} />}
            />
            <FormControlLabel
              disabled={status !== STATUS.IDLE}
              label={'Autoload subsequent pages'}
              control={<Checkbox checked={autoload} onChange={() => setAutoload(!autoload)} />}
            />
          </>
        )}
        {type === 'FACET' && (
          <>
            <TextField type={'number'} label={'Facet Count'} value={facetCount} onChange={(e) => setFacetCount(Number(e.target.value))} />
            <FormGroup sx={{ pl: 1 }}>
              <FormLabel>Facets to include</FormLabel>
              {facetOptions}
            </FormGroup>
          </>
        )}

        {progressSummary}

        <Stack spacing={1}>
          <Button disabled={isWorking} type="submit" variant="contained" size="large" endIcon={<CloudDownload />}>
            {isWorking ? `Importing...` : `Import`}
          </Button>

          {isWorking && (
            <Button
              type="button"
              variant="outlined"
              // size="large"
              color="error"
              endIcon={<Cancel />}
              onClick={(e) => stop(STOP_REASON.CANCELLED)}
            >
              {'Cancel'}
            </Button>
          )}
          {/* { importComplete && <Alert severity="success">This is a success alert — check it out!</Alert>} */}
        </Stack>
      </Stack>
    </Container>
  )
}

export default Import

